import { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, TrendingUp, Users, DollarSign, Award, ChevronRight, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/auth-context";
import { useLeaderboard } from "@/hooks/use-leaderboard-mutations";
import { useEntries } from "@/hooks/use-entries";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from "recharts";

const COLORS = ["hsl(210,100%,45%)", "hsl(170,80%,45%)", "hsl(25,100%,55%)", "hsl(330,100%,60%)", "hsl(270,100%,65%)", "hsl(145,100%,50%)", "hsl(45,100%,50%)", "hsl(0,84%,60%)", "hsl(200,80%,50%)", "hsl(120,60%,40%)"];

const ReportPage = () => {
  const { id: boardId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: leaderboard, isLoading: leaderboardLoading, error: leaderboardError } = useLeaderboard(user?.venueId, boardId);
  const { data: entries = [], isLoading: entriesLoading } = useEntries(user?.venueId, boardId);

  const isLoading = leaderboardLoading || entriesLoading;
  const error = leaderboardError;

  const [staffFilter, setStaffFilter] = useState("");
  const [selectedStaff, setSelectedStaff] = useState<string | null>(null);

  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      if (staffFilter && entry.name !== staffFilter) return false;
      return true;
    });
  }, [staffFilter, entries]);

  const staffDetailSales = useMemo(() => {
    if (!selectedStaff) return [];
    return filteredEntries.filter((e) => e.name === selectedStaff).map((entry, idx) => ({
      id: `${entry.id}-${idx}`,
      staffName: entry.name,
      timestamp: entry.id,
      product: "Membership",
      quantity: entry.membershipsSold,
      value: entry.valueSold || 0,
      guestName: "",
      guestEmail: "",
      orderId: entry.id,
    }));
  }, [selectedStaff, filteredEntries]);

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  if (error || !leaderboard) {
    return (
      <div className="mb-6 flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/app")}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        <div className="flex-1 rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">
            Failed to load leaderboard. Please try again.
          </p>
        </div>
      </div>
    );
  }

  const staffPerformance = entries.map((entry) => ({
    name: entry.name,
    avatar: entry.avatar,
    sales: entry.membershipsSold,
    value: entry.valueSold || 0,
    transactions: 1,
  }));
  const totalMemberships = filteredEntries.reduce((a, e) => a + e.membershipsSold, 0);
  const totalValue = filteredEntries.reduce((a, e) => a + (e.valueSold || 0), 0);
  const uniqueStaff = [...new Set(entries.map((e) => e.name))];
  const productBreakdown = totalMemberships > 0 ? [{ name: "Total", count: totalMemberships, value: totalValue }] : [];
  const dailyTrend = totalMemberships > 0 ? [{ date: "All Time", sales: totalMemberships, value: totalValue }] : [];

  const topPerformer = staffPerformance[0];
  const periodDisplay = leaderboard.period === "weekly" ? "Weekly" : leaderboard.period === "monthly" ? "Monthly" : "All Time";

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/app")}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Campaigns
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{leaderboard.name}</h1>
          <p className="text-sm text-muted-foreground">{periodDisplay} • {entries.length} participants</p>
        </div>
        <Button variant="outline" size="sm">
          <Download className="mr-2 h-4 w-4" /> Export CSV
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          { label: "Memberships Sold", value: totalMemberships.toString(), icon: TrendingUp, color: "text-primary" },
          { label: "Revenue Generated", value: `$${totalValue.toLocaleString()}`, icon: DollarSign, color: "text-neon-green" },
          { label: "Staff Participating", value: staffPerformance.length.toString(), icon: Users, color: "text-neon-orange" },
          { label: "Top Performer", value: topPerformer?.name || "—", icon: Award, color: "text-neon-cyan" },
        ].map((c) => (
          <div key={c.label} className="rounded-xl border bg-card p-5">
            <div className="flex items-center gap-3">
              <c.icon className={`h-5 w-5 ${c.color}`} />
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</p>
            </div>
            <p className="mt-2 text-2xl font-bold">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="mb-8 grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border bg-card p-5">
          <h3 className="mb-4 font-semibold">Sales by Staff Member</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={staffPerformance} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis dataKey="name" type="category" width={110} stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <Tooltip
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "hsl(var(--foreground))" }}
              />
              <Bar dataKey="sales" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border bg-card p-5">
          <h3 className="mb-4 font-semibold">Revenue by Product</h3>
          <ResponsiveContainer width="100%" height={280}>
            {productBreakdown.length > 0 ? (
              <PieChart>
                <Pie data={productBreakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={true} fontSize={11}>
                  {productBreakdown.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  formatter={(value: number) => [`$${value.toLocaleString()}`, "Revenue"]}
                />
              </PieChart>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm text-muted-foreground">No data available</p>
              </div>
            )}
          </ResponsiveContainer>
        </div>
      </div>

      {/* Daily trend */}
      <div className="mb-8 rounded-xl border bg-card p-5">
        <h3 className="mb-4 font-semibold">Daily Sales Trend</h3>
        <ResponsiveContainer width="100%" height={200}>
          {dailyTrend.length > 0 ? (
            <LineChart data={dailyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <Tooltip
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "hsl(var(--foreground))" }}
              />
              <Line type="monotone" dataKey="sales" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: "hsl(var(--primary))", r: 4 }} />
              <Line type="monotone" dataKey="value" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ fill: "hsl(var(--accent))", r: 4 }} yAxisId={0} />
            </LineChart>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-muted-foreground">No data available</p>
            </div>
          )}
        </ResponsiveContainer>
      </div>

      {/* Staff leaderboard table */}
      <div className="mb-8 rounded-xl border bg-card">
        <div className="border-b p-5">
          <h3 className="font-semibold">Staff Performance Breakdown</h3>
          <p className="text-sm text-muted-foreground">Click a staff member to see their details</p>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-5 py-3 font-medium">Rank</th>
                <th className="px-5 py-3 font-medium">Staff Member</th>
                <th className="px-5 py-3 font-medium text-right">Units Sold</th>
                <th className="px-5 py-3 font-medium text-right">Revenue</th>
                <th className="px-5 py-3 font-medium text-right">Transactions</th>
                <th className="px-5 py-3 font-medium text-right">Avg Sale</th>
                <th className="px-5 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {staffPerformance.map((s, i) => (
                <tr
                  key={s.name}
                  onClick={() => setSelectedStaff(s.name)}
                  className={`cursor-pointer border-b last:border-0 transition-colors hover:bg-muted/50 ${selectedStaff === s.name ? "bg-primary/5" : ""}`}
                >
                  <td className="px-5 py-3">
                    <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${i < 3 ? "bg-primary/10 text-primary" : "text-muted-foreground"}`}>
                      {i + 1}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <img src={s.avatar} alt={s.name} className="h-8 w-8 rounded-full object-cover" />
                      <span className="font-medium">{s.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right font-semibold">{s.sales}</td>
                  <td className="px-5 py-3 text-right font-semibold">${s.value.toLocaleString()}</td>
                  <td className="px-5 py-3 text-right text-muted-foreground">{s.transactions}</td>
                  <td className="px-5 py-3 text-right text-muted-foreground">${Math.round(s.value / (s.transactions || 1))}</td>
                  <td className="px-5 py-3">
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Staff detail panel */}
      {selectedStaff && (
        <div className="mb-8 rounded-xl border bg-card">
          <div className="flex items-center justify-between border-b p-5">
            <div className="flex items-center gap-3">
              <img
                src={staffPerformance.find((s) => s.name === selectedStaff)?.avatar || ""}
                alt={selectedStaff}
                className="h-10 w-10 rounded-full object-cover"
              />
              <div>
                <h3 className="font-semibold">{selectedStaff} — Performance Detail</h3>
                <p className="text-sm text-muted-foreground">
                  {staffDetailSales.length} transaction{staffDetailSales.length !== 1 ? "s" : ""} · ${staffDetailSales.reduce((a, e) => a + e.value, 0).toLocaleString()} total
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSelectedStaff(null)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          {staffDetailSales.length > 0 ? (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="px-5 py-3 font-medium">ID</th>
                    <th className="px-5 py-3 font-medium">Product</th>
                    <th className="px-5 py-3 font-medium text-right">Qty</th>
                    <th className="px-5 py-3 font-medium text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {staffDetailSales.map((e) => (
                    <tr key={e.id} className="cursor-pointer border-b last:border-0 transition-colors hover:bg-muted/50">
                      <td className="px-5 py-3 text-muted-foreground font-mono text-xs">{e.orderId.slice(0, 8)}</td>
                      <td className="px-5 py-3">
                        <Badge variant="secondary">{e.product}</Badge>
                      </td>
                      <td className="px-5 py-3 text-right">{e.quantity}</td>
                      <td className="px-5 py-3 text-right font-semibold">${e.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-5 text-center text-sm text-muted-foreground">
              No transaction data available
            </div>
          )}
        </div>
      )}

      {/* All entries log */}
      <div className="rounded-xl border bg-card">
        <div className="flex flex-wrap items-center gap-3 border-b p-5">
          <h3 className="mr-auto font-semibold">Leaderboard Entries</h3>
          <select
            className="rounded-lg border bg-background px-3 py-2 text-sm"
            value={staffFilter}
            onChange={(e) => setStaffFilter(e.target.value)}
          >
            <option value="">All Staff</option>
            {uniqueStaff.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-5 py-3 font-medium">Rank</th>
                <th className="px-5 py-3 font-medium">Staff Member</th>
                <th className="px-5 py-3 font-medium text-right">Memberships</th>
                <th className="px-5 py-3 font-medium text-right">Value</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((e, idx) => (
                <tr key={e.id} className="cursor-pointer border-b last:border-0 transition-colors hover:bg-muted/50">
                  <td className="px-5 py-3">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold bg-primary/10 text-primary">
                      {idx + 1}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-medium">{e.name}</td>
                  <td className="px-5 py-3 text-right">{e.membershipsSold}</td>
                  <td className="px-5 py-3 text-right font-semibold">${(e.valueSold || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReportPage;
